import type React from "react";
import type {PtV2DistributionRow} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Classes.ts";
import {BASELINE_DAYS} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Calc.ts";
import {formatEur, formatInt, termDays} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Format.ts";
import {PtV2Card} from "~/experiments/pages/payment-terms-v2/components/PtV2Card.tsx";

const SHADES = ['var(--ptv2-accent-ink)', 'var(--ptv2-accent)', 'var(--ptv2-accent-soft)', 'var(--ptv2-faint)'];

type Props = {
    rows: PtV2DistributionRow[];
    totalSpend: number | undefined;
    totalInvoices: number | undefined;
};

/** Spend on ERP terms shorter than the baseline, split by term, largest first. */
export const PtV2FastSpendCard: React.FC<Props> = ({rows, totalSpend, totalInvoices}) => {
    const fast = rows
        .map(r => ({...r, days: termDays(r.termCode)}))
        .filter((r): r is PtV2DistributionRow & {days: number} => r.days != null && r.days < BASELINE_DAYS && r.spend != null)
        .sort((a, b) => (b.spend ?? 0) - (a.spend ?? 0));
    const sum = fast.reduce((s, r) => s + (r.spend ?? 0), 0) || 1;
    const color = (i: number) => SHADES[Math.min(i, SHADES.length - 1)];

    return <PtV2Card title="Spend paid faster than Net 60">
        <div className="ptv2-bigfig">
            <span className="ptv2-num">{formatEur(totalSpend)}</span>
            <span className="ptv2-muted">across {formatInt(totalInvoices)} invoices</span>
        </div>
        <div className="ptv2-stack" role="img" aria-label="Share of fast-paid spend by term">
            {fast.map((r, i) => <span key={r.termCode} style={{width: `${(r.spend ?? 0) / sum * 100}%`, background: color(i)}}/>)}
        </div>
        <div className="ptv2-rows">
            {fast.map((r, i) => <div className="ptv2-row" key={r.termCode}>
                <i className="ptv2-swatch" style={{background: color(i)}}/>
                <div className="ptv2-row-main">
                    <b>{r.termCode}</b> <span className="ptv2-muted">{r.label}</span>
                    <div>{formatInt(r.invoiceCount)} invoices · {BASELINE_DAYS - r.days} days short of baseline</div>
                </div>
                <b className="ptv2-num">{formatEur(r.spend)}</b>
            </div>)}
        </div>
    </PtV2Card>;
};
